// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
//
// The left column's header (Figma November 2026, frame 0b): Back, the Shared
// Chat / Comments switch and the add-comment button, or while a comparison
// shows its changes, Back and the changes heading.
import { CaretLeft, ChatCircle, Plus } from "@phosphor-icons/react";
import { Badge, Button, IconButton, Tooltip } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { AskV2ReviewPaneTabs } from "./AskV2ReviewPaneTabs";
import { type AskV2ReviewLeftPane } from "./useAskV2ReviewWorkspace";

export type AskV2ReviewCommentModeControl = {
  /** The artwork is waiting for the click that places a new comment. */
  on: boolean;
  toggle: () => void;
};

export function AskV2ReviewLeftHeader({
  leftPane,
  commentCount,
  onPaneChange,
  changesCount,
  commentMode,
  onBack,
}: {
  leftPane: AskV2ReviewLeftPane;
  /** The Comments tab's badge: every finding the list draws and the pins show. */
  commentCount: number;
  onPaneChange: (pane: AskV2ReviewLeftPane) => void;
  /** Set while a comparison shows its changes; the switch steps aside for them. */
  changesCount?: number;
  /** Absent for read-only viewers, who cannot add a comment. */
  commentMode?: AskV2ReviewCommentModeControl;
  onBack: () => void;
}) {
  const { t } = useTranslation("ui");
  const addLabel = commentMode?.on
    ? t("assetViewer.cancelNewComment")
    : t("assetViewer.addComment");

  return (
    <div
      // why: h-12 is PANE_HEADER_HEIGHT_CLASS; ask-v2 cannot import the V1 module that exports it
      className="flex h-12 shrink-0 items-center gap-2 border-b border-puntt-neutral-gray-5 px-2.5"
      data-testid="ask-v2-review-left-header"
    >
      <Button
        variant="ghost"
        color="gray"
        size="1"
        data-testid="output-back-button"
        aria-label={t("askV2.review.backToChat")}
        onClick={onBack}
      >
        <CaretLeft size={14} weight="bold" aria-hidden="true" />
        {t("askV2.review.back")}
      </Button>
      {changesCount != null ? (
        <h2
          className="m-0 flex items-center gap-2 text-[14px] font-semibold text-puntt-neutral-gray-12"
          data-testid="ask-v2-review-compare-changes-heading"
        >
          {t("askV2.review.compareChangesHeading")}
          <Badge size="1" variant="soft" color="blue">
            {changesCount}
          </Badge>
        </h2>
      ) : (
        <>
          <AskV2ReviewPaneTabs
            data-testid="ask-v2-review-pane-tabs"
            leftPane={leftPane}
            // why: the badge counts every finding the list draws and the pins
            // show. Counting `review.comments` instead let the tab read
            // "Comments (3)" beside a list of 2 after a reject.
            commentCount={commentCount}
            onChange={onPaneChange}
          />
          {commentMode ? (
            <Tooltip content={addLabel}>
              <IconButton
                type="button"
                size="1"
                variant={commentMode.on ? "soft" : "outline"}
                color="gray"
                aria-label={addLabel}
                aria-pressed={commentMode.on}
                data-testid="ask-v2-review-add-comment"
                onClick={commentMode.toggle}
              >
                <span className="relative inline-flex size-[14px] items-center justify-center">
                  <ChatCircle
                    size={14}
                    weight={commentMode.on ? "fill" : "regular"}
                    aria-hidden="true"
                  />
                  <Plus
                    size={8}
                    weight="bold"
                    aria-hidden="true"
                    className={[
                      "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
                      commentMode.on ? "text-base-white" : "",
                    ].join(" ")}
                  />
                </span>
              </IconButton>
            </Tooltip>
          ) : null}
        </>
      )}
    </div>
  );
}
